import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Character } from "@/mocks/characters";
import { supabase, rowToCharacter, CharacterRow } from "@/lib/supabase";

const PAGE_SIZE = 24;

type AgeGroup = "all" | "teens" | "20s" | "30s" | "40s";

const AGE_GROUPS: { key: AgeGroup; label: string; range?: [number, number] }[] = [
  { key: "all", label: "すべて" },
  { key: "teens", label: "10代", range: [10, 19] },
  { key: "20s", label: "20代", range: [20, 29] },
  { key: "30s", label: "30代", range: [30, 39] },
  { key: "40s", label: "40代", range: [40, 49] },
];

const CharactersPage = () => {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [ageGroup, setAgeGroup] = useState<AgeGroup>("all");
  const [imageIndexes, setImageIndexes] = useState<Record<string, number>>({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    supabase
      .from("characters")
      .select("*")
      .order("sort_order", { ascending: true })
      .then(({ data }) => {
        if (data) {
          const chars = (data as CharacterRow[]).map(rowToCharacter) as Character[];
          const init: Record<string, number> = {};
          chars.forEach((c) => { init[c.id] = 0; });
          setImageIndexes(init);
          setCharacters(chars);
        }
        setLoading(false);
      });
  }, []);

  const types = useMemo(
    () => ["all", ...Array.from(new Set(characters.map((c) => c.type)))],
    [characters]
  );

  // Count per age group for badges
  const ageGroupCounts = useMemo(() => {
    const active = characters.filter((c) => c.isActive === true);
    const counts: Record<AgeGroup, number> = { all: active.length, teens: 0, "20s": 0, "30s": 0, "40s": 0 };
    active.forEach((c) => {
      if (c.age == null) return;
      if (c.age >= 10 && c.age <= 19) counts.teens++;
      else if (c.age >= 20 && c.age <= 29) counts["20s"]++;
      else if (c.age >= 30 && c.age <= 39) counts["30s"]++;
      else if (c.age >= 40 && c.age <= 49) counts["40s"]++;
    });
    return counts;
  }, [characters]);

  const filtered = useMemo(() => {
    return characters.filter((c) => {
      if (c.isActive !== true) return false;
      if (typeFilter !== "all" && c.type !== typeFilter) return false;
      if (ageGroup !== "all") {
        const group = AGE_GROUPS.find((g) => g.key === ageGroup);
        if (group?.range) {
          const [min, max] = group.range;
          if (c.age == null || c.age < min || c.age > max) return false;
        }
      }
      return true;
    });
  }, [characters, typeFilter, ageGroup]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleTypeFilterChange = (type: string) => {
    setTypeFilter(type);
    setPage(1);
  };

  const handleAgeGroupChange = (group: AgeGroup) => {
    setAgeGroup(group);
    setPage(1);
  };

  const cycleImage = (charId: string, total: number, e: React.MouseEvent) => {
    e.preventDefault();
    setImageIndexes((prev) => ({
      ...prev,
      [charId]: ((prev[charId] ?? 0) + 1) % total,
    }));
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Nunito', sans-serif" }}>
      <Navbar />
      <div className="pt-16">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-400 to-rose-400 py-10 md:py-16 text-center text-white px-4">
          <h1
            className="text-2xl md:text-4xl font-bold mb-2"
            style={{ fontFamily: "'Pacifico', cursive" }}
          >
            Girls&apos; List
          </h1>
          <p className="text-pink-100 text-sm">あなたにぴったりの女の子を見つけよう♡</p>
          {!loading && (
            <p className="text-pink-200 text-xs mt-1">{filtered.length}人が登録中</p>
          )}
        </div>

        <div className="max-w-7xl mx-auto px-3 md:px-6 py-6 md:py-10">
          {/* Age group filter */}
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-400 mb-2 flex items-center gap-1.5">
              <i className="ri-user-line text-pink-400"></i>年代で絞り込む
            </p>
            <div className="flex gap-2 flex-wrap">
              {AGE_GROUPS.map((group) => {
                const count = ageGroupCounts[group.key];
                if (group.key !== "all" && count === 0) return null;
                return (
                  <button
                    key={group.key}
                    onClick={() => handleAgeGroupChange(group.key)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      ageGroup === group.key
                        ? "bg-pink-500 text-white"
                        : "bg-pink-50 text-pink-500 hover:bg-pink-100"
                    }`}
                  >
                    {group.label}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                      ageGroup === group.key
                        ? "bg-white/20 text-white"
                        : "bg-pink-100 text-pink-400"
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Type filter tabs */}
          <div className="mb-5 md:mb-8">
            <p className="text-xs font-semibold text-gray-400 mb-2 flex items-center gap-1.5">
              <i className="ri-price-tag-3-line text-pink-400"></i>タイプで絞り込む
            </p>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {types.map((type) => (
                <button
                  key={type}
                  onClick={() => handleTypeFilterChange(type)}
                  className={`px-4 py-2 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer flex-shrink-0 ${
                    typeFilter === type
                      ? "bg-rose-500 text-white"
                      : "bg-rose-50 text-rose-500 hover:bg-rose-100"
                  }`}
                >
                  {type === "all" ? "すべて" : type}
                </button>
              ))}
            </div>
          </div>

          {/* Active filters summary */}
          {(ageGroup !== "all" || typeFilter !== "all") && (
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="text-xs text-gray-500">絞り込み中：</span>
              {ageGroup !== "all" && (
                <span className="flex items-center gap-1 bg-pink-100 text-pink-600 text-xs px-2.5 py-1 rounded-full">
                  {AGE_GROUPS.find((g) => g.key === ageGroup)?.label}
                  <button onClick={() => { setAgeGroup("all"); setPage(1); }} className="cursor-pointer hover:text-pink-800">
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </span>
              )}
              {typeFilter !== "all" && (
                <span className="flex items-center gap-1 bg-rose-100 text-rose-600 text-xs px-2.5 py-1 rounded-full">
                  {typeFilter}
                  <button onClick={() => { setTypeFilter("all"); setPage(1); }} className="cursor-pointer hover:text-rose-800">
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </span>
              )}
              <button
                onClick={() => { setAgeGroup("all"); setTypeFilter("all"); setPage(1); }}
                className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer underline"
              >
                すべてリセット
              </button>
            </div>
          )}

          {/* Count & page info */}
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <p className="text-xs md:text-sm text-gray-500">
              {filtered.length}人中 {filtered.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0}〜{Math.min(page * PAGE_SIZE, filtered.length)}人を表示
            </p>
            {totalPages > 1 && (
              <p className="text-xs text-gray-400">
                {page} / {totalPages} ページ
              </p>
            )}
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <i className="ri-loader-4-line text-pink-400 text-3xl animate-spin"></i>
                <p className="text-sm text-gray-400">読み込み中...</p>
              </div>
            </div>
          )}

          {/* Grid — 2 cols mobile, 3 sm, 4 lg, 5 xl */}
          {!loading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5 md:gap-4">
              {paginated.map((char) => {
                const imgIdx = imageIndexes[char.id] ?? 0;
                return (
                  <Link
                    key={char.id}
                    to={`/profile/${char.id}`}
                    className="group bg-white rounded-2xl overflow-hidden border border-pink-100 hover:border-pink-300 transition-all cursor-pointer"
                  >
                    {/* Image area */}
                    <div className="relative w-full overflow-hidden" style={{ paddingBottom: "130%" }}>
                      <img
                        src={char.images[imgIdx]}
                        alt={char.name}
                        className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                      {/* Image cycle button */}
                      {char.images.length > 1 && (
                        <button
                          onClick={(e) => cycleImage(char.id, char.images.length, e)}
                          className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-white/80 rounded-full text-pink-500 hover:bg-white transition-all cursor-pointer"
                        >
                          <i className="ri-image-line text-xs"></i>
                        </button>
                      )}

                      {/* Online badge */}
                      <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/40 backdrop-blur-sm text-white text-xs px-1.5 py-0.5 rounded-full">
                        <span className="w-1 h-1 bg-green-400 rounded-full animate-pulse flex-shrink-0"></span>
                        <span className="text-xs">ON</span>
                      </div>

                      {/* Age badge */}
                      <div className="absolute top-2 right-2 bg-white/90 text-pink-600 text-xs px-1.5 py-0.5 rounded-full font-black">
                        {char.age != null ? `${char.age}歳` : "ひみつ"}
                      </div>

                      {/* Bottom info overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-2 md:p-2.5">
                        <h3 className="text-white font-black text-xs md:text-sm leading-tight mb-0.5 truncate">
                          {char.name}
                        </h3>
                        <div className="flex items-center justify-between">
                          <span className="bg-pink-500/80 backdrop-blur-sm text-white text-xs px-1.5 py-0.5 rounded-full truncate max-w-[70%]">
                            {char.type}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card body */}
                    <div className="p-2 md:p-3">
                      <p className="text-xs text-gray-500 line-clamp-1 leading-relaxed mb-1.5">
                        {char.catchphrase}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {char.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="text-xs bg-pink-50 text-pink-400 px-1.5 py-0.5 rounded-full"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                      <div className="mt-2 pt-2 border-t border-pink-50 flex items-center justify-between">
                        <span className="text-xs text-green-500 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block flex-shrink-0"></span>
                          <span className="hidden sm:inline">オンライン</span>
                          <span className="sm:hidden">ON</span>
                        </span>
                        <span className="text-xs text-pink-400 font-semibold flex items-center gap-0.5">
                          詳細 <i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-20 text-gray-400">
              <i className="ri-search-line text-4xl mb-3 block"></i>
              <p className="mb-2">条件に合う女の子が見つかりませんでした</p>
              <button
                onClick={() => { setAgeGroup("all"); setTypeFilter("all"); setPage(1); }}
                className="text-sm text-pink-400 hover:text-pink-600 cursor-pointer underline"
              >
                フィルターをリセット
              </button>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8 md:mt-12">
              <button
                onClick={() => { setPage((p) => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                disabled={page === 1}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-pink-200 text-pink-400 hover:bg-pink-50 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce<(number | "...")[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === "..." ? (
                    <span key={`ellipsis-${i}`} className="text-gray-400 text-sm px-1">...</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => { setPage(p as number); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-all cursor-pointer ${
                        page === p
                          ? "bg-pink-500 text-white"
                          : "border border-pink-200 text-pink-400 hover:bg-pink-50"
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}

              <button
                onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                disabled={page === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-pink-200 text-pink-400 hover:bg-pink-50 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CharactersPage;
